/**
 * Notification (Toast) Composable
 * 
 * Provides a convenient API for showing toast notifications.
 * 
 * @example
 * ```typescript
 * import { useNotification } from '@/composables/ui/useNotification';
 * 
 * export default defineComponent({
 *   setup() {
 *     const notification = useNotification();
 *     
 *     const saveData = async () => {
 *       try {
 *         await someApiCall();
 *         notification.success('Saved!', 'Data saved successfully');
 *       } catch (error) {
 *         notification.error('Error', error.message);
 *       }
 *     };
 *     
 *     return { saveData };
 *   }
 * });
 * ```
 */

import { useStore } from '../core/useStore';

interface NotificationOptions {
  title: string;
  body: string;
  duration?: number;
  dismissible?: boolean;
  deviceName?: string;
  action?: {
    label: string;
    handler: () => void;
  };
}

export function useNotification() {
  const store = useStore();

  const success = (title: string, body: string, duration = 5000) => {
    store.dispatch('notifications/success', {
      title,
      body,
      duration,
      dismissible: true
    });
  };

  const error = (title: string, body: string, duration = 8000) => {
    store.dispatch('notifications/error', {
      title,
      body,
      duration,
      dismissible: true
    });
  };

  const warning = (title: string, body: string, duration = 6000) => {
    store.dispatch('notifications/warning', {
      title,
      body,
      duration,
      dismissible: true
    });
  };

  const info = (title: string, body: string, duration = 5000) => {
    store.dispatch('notifications/info', {
      title,
      body,
      duration,
      dismissible: true
    });
  };

  const custom = (type: 'success' | 'error' | 'warning' | 'info', options: NotificationOptions) => {
    store.dispatch(`notifications/${type}`, options);
  };

  return {
    success,
    error,
    warning,
    info,
    custom
  };
}

